import { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegionContext = createContext(null);

export const REGIONS = {
  ZA: {
    code: 'ZA',
    name: 'South Africa',
    currency: 'ZAR',
    currencySymbol: 'R',
    pricePerUnit: 35,
    paymentGateway: 'payfast',
    flag: '🇿🇦'
  },
  INTERNATIONAL: {
    code: 'INTERNATIONAL',
    name: 'International',
    currency: 'USD',
    currencySymbol: '$',
    pricePerUnit: 12,
    paymentGateway: 'paypal',
    flag: '🌍'
  }
};

export function RegionProvider({ children }) {
  const [region, setRegion] = useState(null);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    initializeRegion();
  }, []);

  const initializeRegion = async () => {
    setLoading(true);
    
    // Check localStorage first
    const savedRegion = localStorage.getItem('bodycorp_region');
    const savedCountry = localStorage.getItem('bodycorp_detected_country');
    
    if (savedRegion) {
      setRegion(REGIONS[savedRegion] || REGIONS.INTERNATIONAL);
      if (savedCountry) {
        setDetectedCountry(JSON.parse(savedCountry));
      }
      setLoading(false);
      return;
    }
    
    // Detect region from API
    try {
      const response = await fetch(`${API_URL}/api/geolocation/detect`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const detectedRegion = REGIONS[data.region] || REGIONS.INTERNATIONAL;
        
        setDetectedCountry({
          code: data.country_code,
          name: data.country_name,
          city: data.city
        });
        setRegion(detectedRegion);
        
        // Save to localStorage
        localStorage.setItem('bodycorp_region', data.region);
        localStorage.setItem('bodycorp_detected_country', JSON.stringify({
          code: data.country_code,
          name: data.country_name,
          city: data.city
        }));
        
        // Show banner for first-time visitors
        if (!localStorage.getItem('bodycorp_region_confirmed')) {
          setShowBanner(true);
        }
      }
    } catch (error) {
      console.error('Region detection error:', error);
      // Default to South Africa if detection fails
      setRegion(REGIONS.ZA);
    }
    
    setLoading(false);
  };

  const changeRegion = async (regionCode, saveToProfile = false) => {
    const newRegion = REGIONS[regionCode] || REGIONS.INTERNATIONAL;
    setRegion(newRegion);
    localStorage.setItem('bodycorp_region', regionCode);
    localStorage.setItem('bodycorp_region_confirmed', 'true');
    setShowBanner(false);
    
    // If user is logged in, save to profile
    if (saveToProfile) {
      try {
        await fetch(`${API_URL}/api/geolocation/user/region`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            region_code: regionCode,
            manual_override: true
          })
        });
      } catch (error) {
        console.error('Error saving region preference:', error);
      }
    }
  };

  const confirmRegion = () => {
    localStorage.setItem('bodycorp_region_confirmed', 'true');
    setShowBanner(false);
  };

  const formatPrice = (amount, options = {}) => {
    if (!region) return `${amount}`;
    
    const { showSymbol = true, showCurrency = false } = options;
    
    const formatted = new Intl.NumberFormat(region.code === 'ZA' ? 'en-ZA' : 'en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
    
    if (showSymbol) {
      return `${region.currencySymbol}${formatted}`;
    }
    if (showCurrency) {
      return `${formatted} ${region.currency}`;
    }
    return formatted;
  };

  const calculateSubscriptionPrice = (units, months = 12) => {
    if (!region) return { subtotal: 0, total: 0 };
    
    const subtotal = region.pricePerUnit * units * months;
    return {
      pricePerUnit: region.pricePerUnit,
      units,
      months,
      subtotal,
      total: subtotal,
      currency: region.currency,
      currencySymbol: region.currencySymbol,
      formatted: formatPrice(subtotal),
      paymentGateway: region.paymentGateway
    };
  };

  const value = {
    region,
    detectedCountry,
    loading,
    showBanner,
    changeRegion,
    confirmRegion,
    formatPrice,
    calculateSubscriptionPrice,
    isInternational: region?.code === 'INTERNATIONAL',
    isSouthAfrica: region?.code === 'ZA',
    paymentGateway: region?.paymentGateway || 'payfast'
  };

  return (
    <RegionContext.Provider value={value}>
      {children}
    </RegionContext.Provider>
  );
}

export function useRegion() {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
}

export default RegionContext;
